/**
 * AtlasBountyBoardModule — the bounty guild's terminal (TOWN LIFE, 2026-09-24).
 *
 * E at a bounty terminal (AtlasTownFixturesModule) opens this window:
 *   BOARD   every open player bounty: the mark, the reward, how many hunters are on it (≤ 3), the time left, the
 *           placer's note. TAKE one (not your own head, not your own posting; two at a time); DROP it; CANCEL your
 *           own while no one has taken it; TRACE a mark you hunt (a blurred last-known position, one per 45 s).
 *   POST    put a price on a player's head: the reward and a 10 % fee are escrowed (paid out whole to the hunter who
 *           downs the mark; refunded if it lapses). A second post on the same mark tops it up.
 *   HIRE    the guild's own hunters for a fixed price by grade — the stronger the hunter, the more it costs.
 *   MINE    what you placed, what you hunt, and what is on YOUR head.
 * The HUD carries a chip while a price is on your head (WANTED) and while you hunt (the last trace's bearing).
 * Levers: `__bountyBoard`.
 */
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { BOUNTY_TIERS } from '../lib/bounty_logic.js';
import {
  BOUNTY_MIN_REWARD, BOUNTY_HOURS_DEFAULT, BOUNTY_HOURS_MIN, BOUNTY_HOURS_MAX, HUNTERS_PER_BOUNTY, bountyFee, untilText, compassOf,
} from '../lib/town_life.js';
import { townName } from '../lib/town_names.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, debounced, ms } from './town/town_ui.js';

interface BountyRow { bountyId: bigint; target: Identity; placedBy: Identity; reward: bigint; fee: bigint; note: string; state: string; claimedBy: Identity; placedAt: { microsSinceUnixEpoch: bigint }; expiresAt: { microsSinceUnixEpoch: bigint } }
interface TakeRow { takeId: bigint; bountyId: bigint; hunter: Identity; state: string }
interface TraceRow { traceKey: string; bountyId: bigint; hunter: Identity; found: boolean; x: number; z: number; blurM: number; near: string; at: { microsSinceUnixEpoch: bigint } }

type Tab = 'board' | 'post' | 'hire' | 'mine';

export class AtlasBountyBoardModule implements IModule {
  readonly name = 'AtlasBountyBoard';
  private win: UiWindow | null = null;
  private station: { stationId: string; settlementId: string } | null = null;
  private tab: Tab = 'board';
  private listEl: HTMLDivElement | null = null;
  private hud: HTMLDivElement | null = null;
  private subbed = false;
  private hudTimer = 0;
  private readonly rerender = debounced(() => { this.renderList(); this.updateHud(); });

  init(_app: App): void {
    bus.on('atlas:open-bounty-board', (p) => this.open(p));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => this.win?.setOpen(false));
    const h = document.createElement('div');
    h.id = 'bounty-hud';
    h.style.cssText = 'position:fixed;top:120px;right:16px;z-index:31;display:none;flex-direction:column;gap:4px;max-width:300px;pointer-events:none;'
      + 'font:600 12.5px var(--ax-font);color:var(--ax-text)';
    document.body.appendChild(h);
    this.hud = h;
    this.hudTimer = window.setInterval(() => this.updateHud(), 2000);
    (window as unknown as Record<string, unknown>).__bountyBoard = {
      open: (stationId = '0', settlementId = '0') => this.open({ stationId, settlementId }),
      close: () => this.win?.setOpen(false),
      tab: (t: Tab) => { this.tab = t; this.render(); },
      state: () => this.state(),
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM player_bounty', 'SELECT * FROM player_bounty_hunter', 'SELECT * FROM player_bounty_trace',
      ]);
    } catch { /* the reconnect path re-subscribes */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: (c: unknown, r: BountyRow) => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const name of ['playerBounty', 'playerBountyHunter', 'playerBountyTrace']) {
      const t = db[name];
      t?.onInsert?.((_c, r) => {
        this.rerender();
        // A new price on MY head: say so once, loudly.
        const me = conn.identity;
        if (name === 'playerBounty' && me && r.target?.isEqual?.(me) && r.state === 'open') {
          bus.emit('atlas:toast', { text: `A bounty of ${r.reward} ⓒ has been placed on your head.`, kind: 'error', ms: 7000 });
        }
      });
      t?.onUpdate?.(() => this.rerender());
      t?.onDelete?.(() => this.rerender());
    }
  }

  // ── Rows ────────────────────────────────────────────────────────────────────
  private bounties(): BountyRow[] {
    const t = (atlas.conn?.db as unknown as { playerBounty?: { iter(): Iterable<BountyRow> } } | undefined)?.playerBounty;
    return t ? [...t.iter()] : [];
  }
  private takes(): TakeRow[] {
    const t = (atlas.conn?.db as unknown as { playerBountyHunter?: { iter(): Iterable<TakeRow> } } | undefined)?.playerBountyHunter;
    return t ? [...t.iter()] : [];
  }
  private traces(): TraceRow[] {
    const t = (atlas.conn?.db as unknown as { playerBountyTrace?: { iter(): Iterable<TraceRow> } } | undefined)?.playerBountyTrace;
    return t ? [...t.iter()] : [];
  }
  private nameOf(id: Identity): string {
    try {
      const a = atlas.conn?.db.account.identity.find(id);
      return a ? (a.displayName || a.username) : id.toHexString().slice(0, 8);
    } catch { return '?'; }
  }
  private usernameOf(id: Identity): string {
    try { return atlas.conn?.db.account.identity.find(id)?.username ?? ''; } catch { return ''; }
  }
  private live(b: BountyRow): boolean { return b.state === 'open' && ms(b.expiresAt) > Date.now(); }
  private huntersOn(bountyId: bigint): TakeRow[] { return this.takes().filter((t) => t.bountyId === bountyId && t.state === 'hunting'); }

  // ── The window ──────────────────────────────────────────────────────────────
  private open(p: { stationId: string; settlementId: string }): void {
    this.station = p;
    if (!this.win) {
      this.win = makeWindow({ id: 'bounty-board', title: 'Bounty Terminal', icon: 'diamond', width: 560, height: 560, minWidth: 440, minHeight: 360 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;gap:10px;padding:10px 12px;overflow:auto;font:13px var(--ax-font);color:var(--ax-text)';
    }
    const sid = Number(p.settlementId);
    this.win.setTitle(sid > 0 ? `Bounty Terminal — ${townName(sid)}` : 'Bounty Terminal');
    this.win.setOpen(true);
    this.render();
  }

  private render(): void {
    const w = this.win;
    if (!w) return;
    const body = w.body;
    body.replaceChildren();
    body.appendChild(tabs([
      { key: 'board', label: 'Board' }, { key: 'post', label: 'Post a bounty' }, { key: 'hire', label: 'Hire a hunter' }, { key: 'mine', label: 'Mine' },
    ], this.tab, (k) => { this.tab = k as Tab; this.render(); }));
    if (this.tab === 'post') { body.appendChild(this.postForm()); this.listEl = null; return; }
    if (this.tab === 'hire') { body.appendChild(this.hireForm()); this.listEl = null; return; }
    this.listEl = el('div', 'display:flex;flex-direction:column;gap:8px');
    body.appendChild(this.listEl);
    this.renderList();
  }

  private renderList(): void {
    const list = this.listEl;
    if (!list || !this.win?.isOpen()) return;
    list.replaceChildren();
    const me = atlas.conn?.identity ?? null;
    const all = this.bounties().filter((b) => this.live(b)).sort((a, b) => Number(b.reward - a.reward));
    if (this.tab === 'board') {
      list.appendChild(head(`Open bounties (${all.length})`));
      if (all.length === 0) list.appendChild(muted('No prices are posted. Post one on the next tab, or hire one of the guild\'s own hunters.'));
      for (const b of all) list.appendChild(this.bountyCard(b, me));
      return;
    }
    // MINE
    const onMe = all.filter((b) => me && b.target.isEqual(me));
    const placed = this.bounties().filter((b) => me && b.placedBy.isEqual(me)).sort((a, b) => ms(b.placedAt) - ms(a.placedAt)).slice(0, 12);
    const hunting = this.takes().filter((t) => me && t.hunter.isEqual(me));
    list.appendChild(head('On your head'));
    if (onMe.length === 0) list.appendChild(muted('No price on your head.'));
    for (const b of onMe) list.appendChild(this.bountyCard(b, me));
    list.appendChild(head('You are hunting'));
    const liveHunts = hunting.filter((t) => t.state === 'hunting');
    if (liveHunts.length === 0) list.appendChild(muted('You hunt no one. Take a bounty from the board.'));
    for (const t of liveHunts) { const b = this.bounties().find((x) => x.bountyId === t.bountyId); if (b) list.appendChild(this.bountyCard(b, me)); }
    const done = hunting.filter((t) => t.state !== 'hunting').slice(-6);
    for (const t of done) {
      const b = this.bounties().find((x) => x.bountyId === t.bountyId);
      list.appendChild(muted(`${b ? this.nameOf(b.target) : `bounty #${t.bountyId}`} — ${t.state === 'won' ? 'you collected it' : t.state === 'fell' ? 'you fell to your mark' : t.state === 'lost' ? 'another hunter collected it' : t.state}`));
    }
    list.appendChild(head('You posted'));
    if (placed.length === 0) list.appendChild(muted('You have posted no bounties.'));
    for (const b of placed) list.appendChild(this.bountyCard(b, me));
  }

  private bountyCard(b: BountyRow, me: Identity | null): HTMLDivElement {
    const c = card();
    const hunters = this.huntersOn(b.bountyId);
    const mineHunt = me ? hunters.find((t) => t.hunter.isEqual(me)) : undefined;
    const top = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
    top.appendChild(el('span', 'font-weight:600;color:var(--ax-hi)', this.nameOf(b.target)));
    top.appendChild(chip(`${b.reward} ⓒ`, 'warn'));
    top.appendChild(chip(`${hunters.length}/${HUNTERS_PER_BOUNTY} hunters`));
    if (b.state === 'open') top.appendChild(chip(`${untilText(ms(b.expiresAt) - Date.now())} left`));
    else top.appendChild(chip(b.state, b.state === 'claimed' ? 'good' : ''));
    c.appendChild(top);
    const sub = `posted by ${this.nameOf(b.placedBy)}${b.note ? ` — “${b.note}”` : ''}`;
    c.appendChild(muted(sub));
    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    const isTarget = !!me && b.target.isEqual(me);
    const isPlacer = !!me && b.placedBy.isEqual(me);
    if (b.state === 'open') {
      if (mineHunt) {
        row.appendChild(btn('Trace', () => void act(r?.tracePlayerBounty?.({ bountyId: b.bountyId }), 'Trace sent — the guild\'s informants report back', () => this.rerender()), true, 'A blurred last-known position of your mark (one every 45 s)'));
        const tr = this.traces().find((t) => t.bountyId === b.bountyId);
        if (tr && tr.found) {
          row.appendChild(btn('Waypoint', () => void act(atlas.conn?.reducers.createWaypoint({ name: `Mark: ${this.nameOf(b.target)}`.slice(0, 32), posX: tr.x, posZ: tr.z }), 'Waypoint set on the last trace')));
        }
        row.appendChild(btn('Drop', () => void act(r?.dropPlayerBounty?.({ bountyId: b.bountyId }), 'You dropped the bounty', () => this.rerender())));
      } else if (!isTarget && !isPlacer) {
        row.appendChild(btn('Take', () => void act(r?.takePlayerBounty?.({ bountyId: b.bountyId }), `You hunt ${this.nameOf(b.target)} — down them to collect ${b.reward} ⓒ`, () => this.rerender()), true));
      }
      if (isPlacer && hunters.length === 0) row.appendChild(btn('Cancel', () => void act(r?.cancelPlayerBounty?.({ bountyId: b.bountyId }), 'Bounty cancelled — the reward is refunded', () => this.rerender())));
      if (isPlacer) row.appendChild(btn('Add to it', () => { this.tab = 'post'; this.render(); const t = this.win?.body.querySelector<HTMLInputElement>('[data-f="target"]'); if (t) t.value = this.usernameOf(b.target); }));
    }
    if (row.childElementCount) c.appendChild(row);
    const tr = mineHunt ? this.traces().find((t) => t.bountyId === b.bountyId) : undefined;
    if (tr) c.appendChild(muted(tr.found
      ? `Last trace ${untilText(Date.now() - ms(tr.at))} ago: ~${Math.round(Math.hypot(tr.x - atlas.localX, tr.z - atlas.localZ))} m ${compassOf(tr.x - atlas.localX, tr.z - atlas.localZ)} (±${Math.round(tr.blurM)} m)${tr.near ? ` near ${this.nearText(tr.near)}` : ''}`
      : `Last trace ${untilText(Date.now() - ms(tr.at))} ago: the mark is out of reach (off-world or offline).`));
    return c;
  }

  private nearText(near: string): string {
    if (near.startsWith('site:')) return townName(Number(near.slice(5)));
    return near;
  }

  private postForm(): HTMLDivElement {
    const f = el('div', 'display:flex;flex-direction:column;gap:9px');
    f.appendChild(head('Put a price on a head'));
    f.appendChild(muted(`The reward and a 10 % guild fee (at least 25 ⓒ) are held by the guild. The first hunter to down the mark collects the whole reward; if the bounty lapses, the reward comes back to you. Up to ${HUNTERS_PER_BOUNTY} hunters may take one bounty.`));
    const target = input('the mark\'s username', '', 'text', '220px');
    target.dataset.f = 'target';
    const reward = input(`reward (≥ ${BOUNTY_MIN_REWARD})`, String(BOUNTY_MIN_REWARD * 2), 'number', '140px');
    const hours = select([6, 12, 24, 48, 72, 120, 168].filter((h) => h >= BOUNTY_HOURS_MIN && h <= BOUNTY_HOURS_MAX).map((h) => ({ value: String(h), label: h < 24 ? `${h} hours` : `${h / 24} days` })), String(BOUNTY_HOURS_DEFAULT));
    const note = input('a note for the hunters (optional)', '', 'text', '100%');
    note.maxLength = 120;
    const cost = el('div', 'color:var(--ax-soft)');
    const upd = (): void => { const v = Math.max(0, Math.floor(Number(reward.value) || 0)); cost.textContent = `You pay ${v + bountyFee(v)} ⓒ now (${v} reward + ${bountyFee(v)} fee).`; };
    reward.addEventListener('input', upd); upd();
    f.appendChild(field('Mark', target));
    f.appendChild(field('Reward', reward, cost));
    f.appendChild(field('Lasts', hours));
    f.appendChild(field('Note', note));
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    f.appendChild(btn('Post the bounty', () => {
      const v = Math.floor(Number(reward.value) || 0);
      void act(r?.postPlayerBounty?.({ targetUsername: target.value.trim(), reward: BigInt(v), note: note.value.trim(), hours: Number(hours.value) }), `Bounty posted on ${target.value.trim()}`, () => { this.tab = 'mine'; this.render(); });
    }, true));
    return f;
  }

  private hireForm(): HTMLDivElement {
    const f = el('div', 'display:flex;flex-direction:column;gap:9px');
    f.appendChild(head('Hire one of the guild\'s hunters'));
    f.appendChild(muted('The guild sends its own hunter after the mark for a fixed price — the stronger the hunter, the more it costs. Your coin is spent whether or not the hunter succeeds.'));
    const target = input('the mark\'s username', '', 'text', '220px');
    f.appendChild(field('Mark', target));
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    for (const t of BOUNTY_TIERS) {
      const c = card();
      const top = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
      top.appendChild(el('span', 'font-weight:600;color:var(--ax-hi)', t.name));
      top.appendChild(chip(`${t.minSpend} ⓒ`, 'warn'));
      top.appendChild(chip(`${t.hp} hp · ${t.damage} per blow`));
      c.appendChild(top);
      c.appendChild(muted(t.tier === 1 ? 'A blade for hire. Strikes only when the odds are clearly its own.' : t.tier === 2 ? 'A patient tracker; closes in on a weakened mark.' : t.tier === 3 ? 'Relentless and hard to shake.' : 'The guild\'s last word: few survive it.'));
      c.appendChild(btn(`Hire for ${t.minSpend} ⓒ`, () => void act(r?.hireBountyHunter?.({ targetUsername: target.value.trim(), tier: t.tier }), `${t.name} hired — it is already on the road`), true));
      f.appendChild(c);
    }
    return f;
  }

  // ── The HUD chip ────────────────────────────────────────────────────────────
  private updateHud(): void {
    const h = this.hud;
    const me = atlas.conn?.identity;
    if (!h || !me || atlas.currentWorldId === null) { if (h) h.style.display = 'none'; return; }
    const lines: string[] = [];
    let onMe = 0n, hunters = 0;
    for (const b of this.bounties()) if (this.live(b) && b.target.isEqual(me)) { onMe += b.reward; hunters += this.huntersOn(b.bountyId).length; }
    if (onMe > 0n) lines.push(`WANTED — ${onMe} ⓒ on your head${hunters ? ` · ${hunters} hunter${hunters > 1 ? 's' : ''}` : ''}`);
    for (const t of this.takes()) {
      if (t.state !== 'hunting' || !t.hunter.isEqual(me)) continue;
      const b = this.bounties().find((x) => x.bountyId === t.bountyId);
      if (!b || !this.live(b)) continue;
      const tr = this.traces().find((x) => x.bountyId === t.bountyId && x.found);
      lines.push(tr
        ? `Hunting ${this.nameOf(b.target)} — ~${Math.round(Math.hypot(tr.x - atlas.localX, tr.z - atlas.localZ))} m ${compassOf(tr.x - atlas.localX, tr.z - atlas.localZ)} (${untilText(Date.now() - ms(tr.at))} ago)`
        : `Hunting ${this.nameOf(b.target)} — trace them at a bounty terminal`);
    }
    if (lines.length === 0) { h.style.display = 'none'; return; }
    h.style.display = 'flex';
    const sig = lines.join('\n');
    if (h.dataset.sig === sig) return;
    h.dataset.sig = sig;
    // Built with textContent: a player's display name is never parsed as markup.
    h.replaceChildren(...lines.map((l) => el('div', `padding:5px 10px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 84%, transparent);border:1px solid color-mix(in srgb, var(--ax-${l.startsWith('WANTED') ? 'bad' : 'warn'}) 55%, transparent)`, l)));
  }

  private state(): Record<string, unknown> {
    const me = atlas.conn?.identity;
    return {
      open: !!this.win?.isOpen(), tab: this.tab, station: this.station,
      bounties: this.bounties().filter((b) => this.live(b)).map((b) => ({ id: String(b.bountyId), target: this.nameOf(b.target), reward: Number(b.reward), hunters: this.huntersOn(b.bountyId).length, onMe: !!me && b.target.isEqual(me) })),
      hud: this.hud?.style.display !== 'none' ? this.hud?.textContent ?? '' : '',
    };
  }

  dispose(): void {
    window.clearInterval(this.hudTimer);
    this.hud?.remove();
  }
}
